<template>
  <div class="teams-refund-view container py-4">
   


    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Daten werden geladen...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <div v-else-if="!team" class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Team nicht gefunden.
    </div>

    <div v-else>
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <font-awesome-icon icon="money-bill-wave" class="me-2" />
            Erstattung
          </h2>
          <p class="text-muted mb-0">Konfiguriere deine Erstattungsdaten für {{ team.name }}</p>
        </div>
        <BackButton fallback="teams-settings" :params="{ id: teamId }" />
      </div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-4">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
            Bereiche
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-detail', params: { id: teamId } }"
            class="text-decoration-none"
          >
            Quittungsverwaltung
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-settings', params: { id: teamId } }"
            class="text-decoration-none"
          >
            Bereich-Einstellungen
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Erstattung</li>
      </ol>
    </nav>

      <!-- Team Status Warning -->
      <div v-if="!canAccessRefund" class="alert alert-gradient-warning mb-4" role="alert">
        <div class="d-flex align-items-center">
          <font-awesome-icon icon="lock" class="fa-2x me-3" />
          <div>
            <h5 class="alert-heading mb-1">Erstattungsfunktion nicht verfügbar</h5>
            <p class="mb-2">Die Erstattungsfunktion ist nur für Pro und Ultimate Teams verfügbar.</p>
          </div>
        </div>
      </div>

      <!-- Refund Settings Not Enabled -->
      <div v-else-if="!refundSettings.enabled" class="alert alert-info mb-4" role="alert">
        <div class="d-flex align-items-center">
          <font-awesome-icon icon="info-circle" class="fa-2x me-3" />
          <div>
            <h5 class="alert-heading mb-1">Erstattung nicht aktiviert</h5>
            <p class="mb-2">Die Erstattungsfunktion wurde für dieses Team noch nicht aktiviert. Bitte wende dich an einen Team-Administrator.</p>
          </div>
        </div>
      </div>

      <!-- No Available Methods -->
      <div v-else-if="!hasAvailableMethods" class="alert alert-info mb-4" role="alert">
        <div class="d-flex align-items-center">
          <font-awesome-icon icon="info-circle" class="fa-2x me-3" />
          <div>
            <h5 class="alert-heading mb-1">Keine Erstattungsmethoden verfügbar</h5>
            <p class="mb-2">Es wurden noch keine Erstattungsmethoden für dieses Team konfiguriert. Bitte wende dich an einen Team-Administrator.</p>
          </div>
        </div>
      </div>

      <!-- User Refund Data Form -->
      <div v-else class="card">
        <div class="card-header">
          <h5 class="mb-0">
            <font-awesome-icon icon="user" class="me-2" />
            Meine Erstattungsdaten
          </h5>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveUserRefundData">
            <div class="mb-3">
              <label for="refundMethod" class="form-label fw-semibold">
                Erstattungsmethode
                <span class="text-danger">*</span>
              </label>
              <select
                id="refundMethod"
                class="form-select"
                v-model="userRefundData.refundMethod"
                required
              >
                <option value="">Bitte wähle eine Methode</option>
                <option v-if="refundSettings.paypalEnabled" value="paypal">PayPal</option>
                <option v-if="refundSettings.bankTransferEnabled" value="bank_transfer">Banküberweisung</option>
                <option v-if="refundSettings.cashEnabled" value="cash">Bar</option>
              </select>
            </div>

            <!-- PayPal Fields -->
            <div v-if="userRefundData.refundMethod === 'paypal'" class="mb-3">
              <label for="paypalMeName" class="form-label fw-semibold">
                PayPal.me Name
                <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <input
                  id="paypalMeName"
                  type="text"
                  class="form-control"
                  :class="{
                    'is-valid': paypalValidation.valid,
                    'is-invalid': paypalValidation.error && userRefundData.paypalMeName.length > 0
                  }"
                  v-model="userRefundData.paypalMeName"
                  placeholder="Dein PayPal.me Name (z.B. max.mustermann)"
                  required
                  @input="resetPayPalValidation"
                />
                <button 
                  type="button" 
                  class="btn btn-outline-secondary" 
                  @click="validatePayPalMeName(userRefundData.paypalMeName)"
                  :disabled="!userRefundData.paypalMeName || paypalValidation.loading"
                >
                  <font-awesome-icon 
                    v-if="paypalValidation.loading" 
                    icon="spinner" 
                    class="fa-spin me-1" 
                  />
                  <font-awesome-icon 
                    v-else 
                    icon="check" 
                    class="me-1" 
                  />
                  {{ paypalValidation.loading ? 'Prüfe...' : 'Prüfen' }}
                </button>
              </div>
              
              <!-- PayPal Validation Feedback -->
              <div v-if="paypalValidation.valid && paypalValidation.data" class="paypal-profile-card my-3">
                <div
                  class="paypal-profile-bg"
                  :style="paypalValidation.data.backgroundImage ? `background-image: url('${paypalValidation.data.backgroundImage}')` : ''"
                ></div>
                <div class="paypal-profile-content">
                  <div class="paypal-profile-avatar">
                    <template v-if="paypalValidation.data.profilePicture">
                      <img :src="paypalValidation.data.profilePicture" alt="Profilbild" />
                    </template>
                    <template v-else>
                      <font-awesome-icon icon="user" class="fa-2x text-primary" />
                    </template>
                  </div>
                  <div class="paypal-profile-info">
                    <div class="bold text-dark">{{ paypalValidation.data.displayName }}</div>
                    <div class="text-muted small">{{ paypalValidation.data.userName }}</div>
                    <div class="text-muted small" v-if="paypalValidation.data.location">{{ paypalValidation.data.location }}</div>
                    <div>
                      <a :href="paypalValidation.data.url" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
                        {{ paypalValidation.data.url }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="paypalValidation.error && userRefundData.paypalMeName.length > 0" class="form-text text-danger">
                {{ paypalValidation.error }}
                <div v-if="paypalValidation.fallback" class="small mt-1">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Versuch es später erneut, wenn die PayPal.me Seite wieder erreichbar ist.
                </div>
              </div>
              
              <div class="form-text">
                <small class="text-muted">
                  Dein PayPal.me Name ohne @paypal.me
                  <br>
                  <a href="https://www.paypal.com/de/cshelp/article/was-ist-paypalme-help432" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     class="text-decoration-none">
                    <font-awesome-icon icon="question-circle" class="me-1" />
                    Was ist PayPal.me? - Hilfe anzeigen
                  </a>
                </small>
              </div>
            </div>

            <!-- Bank Transfer Fields -->
            <div v-if="userRefundData.refundMethod === 'bank_transfer'" class="mb-3">
              <div class="row">
                <div class="col-md-6">
                  <label for="iban" class="form-label fw-semibold">
                    IBAN
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input
                      id="iban"
                      type="text"
                      class="form-control"
                      :class="{
                        'is-valid': ibanValidation.valid,
                        'is-invalid': ibanValidation.error && userRefundData.iban.length > 0
                      }"
                      v-model="userRefundData.iban"
                      placeholder="DE89 3704 0044 0532 0130 00"
                      required
                      @input="resetIbanValidation"
                    />
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary" 
                      @click="validateIban(userRefundData.iban)"
                      :disabled="!userRefundData.iban || ibanValidation.loading"
                    >
                      <font-awesome-icon 
                        v-if="ibanValidation.loading" 
                        icon="spinner" 
                        class="fa-spin me-1" 
                      />
                      <font-awesome-icon 
                        v-else 
                        icon="check" 
                        class="me-1" 
                      />
                      {{ ibanValidation.loading ? 'Prüfe...' : 'Prüfen' }}
                    </button>
                  </div>
                  
                  <!-- IBAN Validation Feedback -->
                  <div v-if="ibanValidation.valid && ibanValidation.bankName" class="form-text text-success">
                    <font-awesome-icon icon="bank" class="me-1" />
                    <strong>{{ ibanValidation.bankName }}</strong>
                  </div>
                  <div v-else-if="ibanValidation.error && userRefundData.iban.length > 0" class="form-text text-danger">
                    {{ ibanValidation.error }}
                  </div>
                  <div v-else class="form-text">
                    Prüfe die IBAN hier oder speichere die Daten, um sie prüfen zu lassen.
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="bic" class="form-label fw-semibold">
                    BIC
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    id="bic"
                    type="text"
                    class="form-control"
                    v-model="userRefundData.bic"
                    placeholder="COBADEFFXXX"
                    :readonly="ibanValidation.valid && ibanValidation.bic"
                    :class="{ 'bg-light': ibanValidation.valid && ibanValidation.bic }"
                    required
                  />
                  <div v-if="ibanValidation.bicAutoFilled" class="form-text text-success">
                    <font-awesome-icon icon="check" class="me-1" />
                    BIC automatisch ausgefüllt
                  </div>
                </div>
              </div>
              <div class="mt-3">
                <label for="accountHolder" class="form-label fw-semibold">
                  Kontoinhaber
                  <span class="text-danger">*</span>
                </label>
                <input
                  id="accountHolder"
                  type="text"
                  class="form-control"
                  v-model="userRefundData.accountHolder"
                  placeholder="Max Mustermann"
                  required
                />
              </div>
            </div>

            <div class="d-flex justify-content-end">
              <button type="submit" class="btn btn-primary" :disabled="isSaveButtonDisabled">
                <font-awesome-icon 
                  :icon="savingUserData ? 'spinner' : 'save'" 
                  :class="{ 'fa-spin': savingUserData }" 
                  class="me-1" 
                />
                {{ savingUserData ? 'Speichern...' : 'Erstattungsdaten speichern' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Info Section -->
      <div v-if="hasAvailableMethods" class="card mt-4">
        <div class="card-header">
          <h6 class="mb-0">
            <font-awesome-icon icon="info-circle" class="me-2" />
            Informationen zu verfügbaren Erstattungsmethoden
          </h6>
        </div>
        <div class="card-body">
          <p class="small text-muted">
            Die Erstattungsfunktion ermöglicht es dir, Erstattungen für Quittungen zu erhalten.
          </p>
          
          <!-- PayPal Information -->
          <div v-if="refundSettings.paypalEnabled" class="mt-3 p-3 bg-light rounded">
            <h6 class="mb-2">
              <font-awesome-icon icon="fab fa-paypal" class="me-2" />
              PayPal.me
            </h6>
            <p class="small text-muted mb-2">
              PayPal.me ermöglicht es dir, einfach Geld über PayPal zu empfangen. Du teilst deinen persönlichen PayPal.me-Link (paypal.me/deinName) mit anderen, die dir dann Geld senden können.
            </p>
            <ul class="small text-muted mb-3">
              <li>Gib deinen PayPal.me Namen ohne @paypal.me an</li>
              <li>Beispiel: Wenn dein Link paypal.me/max.mustermann ist, gib "max.mustermann" ein</li>
            </ul>
            <a href="https://www.paypal.com/de/cshelp/article/was-ist-paypalme-help432" 
               target="_blank" 
               rel="noopener noreferrer"
               class="btn btn-sm btn-outline-primary">
              <font-awesome-icon icon="external-link-alt" class="me-1" />
              PayPal.me Hilfe anzeigen
            </a>
          </div>
          
          <!-- Bank Transfer Information -->
          <div v-if="refundSettings.bankTransferEnabled" class="mt-3 p-3 bg-light rounded">
            <h6 class="mb-2">
              <font-awesome-icon icon="university" class="me-2" />
              Banküberweisung
            </h6>
            <p class="small text-muted mb-2">
              Für Banküberweisungen benötigen wir deine Bankdaten zur korrekten Überweisung.
            </p>
            <ul class="small text-muted mb-3">
              <li><strong>IBAN:</strong> Wird automatisch validiert und die Bank erkannt</li>
              <li><strong>BIC:</strong> Wird automatisch ausgefüllt, falls verfügbar</li>
              <li><strong>Kontoinhaber:</strong> Name des Kontoinhabers (muss mit der IBAN übereinstimmen)</li>
            </ul>
            <p class="small text-muted mb-0">
              <font-awesome-icon icon="info-circle" class="me-1" />
              Deine Bankdaten werden sicher gespeichert und nur für Erstattungen verwendet.
            </p>
          </div>
          
          <!-- Cash Information -->
          <div v-if="refundSettings.cashEnabled" class="mt-3 p-3 bg-light rounded">
            <h6 class="mb-2">
              <font-awesome-icon icon="money-bill-wave" class="me-2" />
              Bar
            </h6>
            <p class="small text-muted mb-2">
              Bei der Bar-Erstattung erhältst du das Geld direkt in bar ausgezahlt.
            </p>
            <ul class="small text-muted mb-0">
              <li>Keine zusätzlichen Daten erforderlich</li>
              <li>Erstattung erfolgt persönlich oder über einen Beauftragten</li>
              <li>Details zur Auszahlung werden vom Team-Administrator festgelegt</li>
            </ul>
          </div>
          
          <!-- General Information -->
          <div class="mt-3 p-3 bg-info bg-opacity-10 border border-info rounded">
            <h6 class="mb-2">
              <font-awesome-icon icon="shield-alt" class="me-2" />
              Datenschutz
            </h6>
            <p class="small text-muted mb-0">
              Deine Erstattungsdaten werden sicher gespeichert und nur für Erstattungen verwendet. 
              Nur Team-Administratoren können diese Daten einsehen.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { teamService } from '@/api/teams'
import { ibanApi } from '@/api/iban'
import { paypalApi } from '@/api/paypal'
import { showInfo, showSuccess, showError } from '@/services/notification'
import BackButton from '@/components/common/BackButton.vue'

const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()


const { selectedTeam } = storeToRefs(teamsStore)


const loading = ref(true)
const error = ref(null)
const refundSettings = ref({
  enabled: false,
  paypalEnabled: false,
  bankTransferEnabled: false,
  cashEnabled: false
})
const userRefundData = ref({
  refundMethod: '',
  paypalMeName: '',
  iban: '',
  bic: '',
  accountHolder: ''
})
const savingUserData = ref(false)
const ibanValidation = ref({
  valid: false,
  loading: false,
  bankName: null,
  bic: null,
  error: null,
  bicAutoFilled: false
})

const paypalValidation = ref({
  valid: false,
  loading: false,
  data: null,
  error: null,
  fallback: false,
  message: null
})


const teamId = computed(() => route.params.id)
const team = computed(() => selectedTeam.value)

const canAccessRefund = computed(() => {
  return team.value?.status === 'pro' || team.value?.status === 'ultimate'
})

const hasAvailableMethods = computed(() => {
  return refundSettings.value.paypalEnabled || 
         refundSettings.value.bankTransferEnabled || 
         refundSettings.value.cashEnabled
})


const isSaveButtonDisabled = computed(() => {
  if (savingUserData.value) return true
  

  if (!userRefundData.value.refundMethod) return true
  

  if (userRefundData.value.refundMethod === 'paypal') {
    if (!userRefundData.value.paypalMeName) return true
    if (paypalValidation.value.loading) return true
    if (!paypalValidation.value.valid && userRefundData.value.paypalMeName.length > 0) return true
  }
  

  if (userRefundData.value.refundMethod === 'bank_transfer') {
    if (!userRefundData.value.iban || !userRefundData.value.bic || !userRefundData.value.accountHolder) return true
    if (ibanValidation.value.loading) return true
  }
  
  return false
})


const normalizeIban = (iban) => (iban || '').toString().toUpperCase().replace(/\s/g, '')
let ibanValidationRequestId = 0

const validateIban = async (iban) => {
  const normalizedIban = normalizeIban(iban)
  const requestId = ++ibanValidationRequestId

  if (!normalizedIban) {
    ibanValidation.value = {
      valid: false,
      loading: false,
      bankName: null,
      bic: null,
      error: null,
      bicAutoFilled: false
    }
    return false
  }

  if (!/^[A-Z0-9]{15,34}$/.test(normalizedIban)) {
    ibanValidation.value = {
      valid: false,
      loading: false,
      bankName: null,
      bic: null,
      error: 'Die IBAN hat kein gültiges Format. Bitte prüfe Länge und Zeichen.',
      bicAutoFilled: false
    }
    return false
  }

  ibanValidation.value = {
    ...ibanValidation.value,
    valid: false,
    loading: true,
    error: null,
    bic: null,
    bicAutoFilled: false
  }

  try {
    const result = await ibanApi.validateIban(normalizedIban)
    if (
      requestId !== ibanValidationRequestId ||
      normalizedIban !== normalizeIban(userRefundData.value.iban)
    ) {
      return false
    }

    if (result.valid === true) {
      const wasBicEmpty = !userRefundData.value.bic
      const bicAutoFilled = Boolean(result.bic && wasBicEmpty)
      ibanValidation.value = {
        valid: true,
        loading: false,
        bankName: result.bankName,
        bic: result.bic,
        error: null,
        bicAutoFilled
      }

      if (result.bic && wasBicEmpty) {
        userRefundData.value.bic = result.bic
      }
      return true
    } else {
      ibanValidation.value = {
        valid: false,
        loading: false,
        bankName: null,
        bic: null,
        error: 'Die IBAN-Prüfziffer stimmt nicht. Bitte prüfe die eingegebenen Stellen.',
        bicAutoFilled: false
      }
      return false
    }
  } catch (error) {
    if (
      requestId !== ibanValidationRequestId ||
      normalizedIban !== normalizeIban(userRefundData.value.iban)
    ) {
      return false
    }

    console.error('IBAN-Validierungsfehler:', error)
    ibanValidation.value = {
      valid: false,
      loading: false,
      bankName: null,
      bic: null,
      error: 'Die IBAN konnte gerade nicht geprüft werden. Bitte versuche es später erneut.',
      bicAutoFilled: false
    }
    return false
  }
}


const validatePayPalMeName = async (paypalMeName) => {
  if (!paypalMeName || paypalMeName.length < 3) {
    paypalValidation.value = {
      valid: false,
      loading: false,
      data: null,
      error: null,
      fallback: false,
      message: null
    }
    return
  }


  paypalValidation.value.loading = true
  paypalValidation.value.error = null
  paypalValidation.value.fallback = false
  paypalValidation.value.message = null

  try {
    const result = await paypalApi.validatePayPalMeName(paypalMeName)
    
    if (result.success) {
      paypalValidation.value = {
        valid: true,
        loading: false,
        data: result.data,
        error: null,
        fallback: result.data?.fallback || false,
        message: result.message || null
      }
    } else {
      paypalValidation.value = {
        valid: false,
        loading: false,
        data: null,
        error: result.error || 'PayPal.me Profil nicht gefunden',
        fallback: result.fallback || false,
        message: null
      }
    }
  } catch (error) {
    console.error('PayPal.me Validierungsfehler:', error)
    paypalValidation.value = {
      valid: false,
      loading: false,
      data: null,
      error: 'Fehler bei der PayPal.me Validierung',
      fallback: false,
      message: null
    }
  }
}


watch(() => userRefundData.value.refundMethod, (newMethod) => {

  if (newMethod !== 'bank_transfer') {
    ibanValidation.value = {
      valid: false,
      loading: false,
      bankName: null,
      bic: null,
      error: null,
      bicAutoFilled: false
    }
  }
  
  if (newMethod !== 'paypal') {
    paypalValidation.value = {
      valid: false,
      loading: false,
      data: null,
      error: null,
      fallback: false,
      message: null
    }
  }
})


const loadRefundSettings = async () => {
  try {
    const settings = await teamService.getTeamRefundSettings(teamId.value)
    refundSettings.value = settings

    const onlyCashEnabled =
      settings.enabled &&
      settings.cashEnabled === true &&
      settings.paypalEnabled === false &&
      settings.bankTransferEnabled === false

    if (onlyCashEnabled && !userRefundData.value.refundMethod) {
      userRefundData.value.refundMethod = 'cash'
    }
  } catch (err) {
    console.error('Fehler beim Laden der Erstattungseinstellungen:', err)
    error.value = 'Fehler beim Laden der Erstattungseinstellungen'
  }
}

const loadUserRefundData = async () => {
  try {
    const data = await teamService.getUserRefundData(teamId.value)
    if (data) {
      userRefundData.value = {
        refundMethod: data.refundMethod || '',
        paypalMeName: data.paypalMeName || '',
        iban: data.iban || '',
        bic: data.bic || '',
        accountHolder: data.accountHolder || ''
      }
    } else {
      const onlyCashEnabled =
        refundSettings.value.enabled &&
        refundSettings.value.cashEnabled === true &&
        refundSettings.value.paypalEnabled === false &&
        refundSettings.value.bankTransferEnabled === false

      if (onlyCashEnabled) {
        userRefundData.value.refundMethod = 'cash'
      }
    }
  } catch (err) {
    console.error('Fehler beim Laden der Benutzer-Erstattungsdaten:', err)

  }
}

const saveUserRefundData = async () => {
  try {
    savingUserData.value = true
    error.value = null

    if (!userRefundData.value.refundMethod) {
      throw new Error('Bitte wähle eine Erstattungsmethode')
    }

    if (userRefundData.value.refundMethod === 'paypal') {
      if (!userRefundData.value.paypalMeName) {
        throw new Error('PayPal.me Name ist erforderlich')
      }
      

      if (!paypalValidation.value.valid) {
        throw new Error('PayPal.me Name muss gültig sein. Bitte warte auf die Validierung oder versuch es später erneut.')
      }
      

      if (paypalValidation.value.fallback) {
        const confirmed = await new Promise((resolve) => {

          resolve(confirm('PayPal.me Validierung erfolgte über Fallback-Modus. Möchtest du trotzdem fortfahren?'))
        })
        
        if (!confirmed) {
          throw new Error('Speicherung abgebrochen')
        }
      }
    }

    if (userRefundData.value.refundMethod === 'bank_transfer') {
      if (!userRefundData.value.iban || !userRefundData.value.bic || !userRefundData.value.accountHolder) {
        throw new Error('IBAN, BIC und Kontoinhaber sind erforderlich')
      }
      

      const ibanIsValid = await validateIban(userRefundData.value.iban)
      if (!ibanIsValid) {
        return
      }
    }


    const dataToSave = { ...userRefundData.value }
    

    if (dataToSave.iban) {
      dataToSave.iban = dataToSave.iban.toUpperCase().replace(/\s/g, '')
    }
    if (dataToSave.bic) {
      dataToSave.bic = dataToSave.bic.toUpperCase().replace(/\s/g, '')
    }

    await teamService.saveUserRefundData(teamId.value, dataToSave)
    

    showSuccess('Erstattungsdaten erfolgreich gespeichert!')
    

    if (route.query.requireSetup === 'true' && route.query.redirect) {

      setTimeout(() => {
        router.push(route.query.redirect)
      }, 1000)
    }
    
  } catch (err) {
    console.error('Fehler beim Speichern der Erstattungsdaten:', err)
    const message = err.response?.data?.error || err.response?.data?.message || err.message || 'Fehler beim Speichern der Erstattungsdaten'
    error.value = message
    if (message.toLowerCase().includes('iban')) {
      ibanValidation.value = {
        ...ibanValidation.value,
        valid: false,
        loading: false,
        error: message
      }
    }
  } finally {
    savingUserData.value = false
  }
}


onMounted(async () => {
  try {
    loading.value = true
    error.value = null
    

    if (!team.value) {
      await teamsStore.loadTeamById(teamId.value)
    }
    

    await Promise.all([
      loadRefundSettings(),
      loadUserRefundData()
    ])
    

    if (route.query.requireSetup === 'true') {
      showInfo('Bitte konfiguriere deine Erstattungsdaten, bevor du fortfahren kannst.')
    }
    
  } catch (err) {
    console.error('Fehler beim Laden der Daten:', err)
    error.value = 'Fehler beim Laden der Daten'
  } finally {
    loading.value = false
  }
})


const resetPayPalValidation = () => {
  paypalValidation.value = {
    valid: false,
    loading: false,
    data: null,
    error: null,
    fallback: false,
    message: null
  }
}


const resetIbanValidation = () => {
  ibanValidationRequestId += 1
  if (ibanValidation.value.bicAutoFilled) {
    userRefundData.value.bic = ''
  }
  ibanValidation.value = {
    valid: false,
    loading: false,
    bankName: null,
    bic: null,
    error: null,
    bicAutoFilled: false
  }
}
</script>
